import { Spinner } from '@lmc/ui-kit'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { chain, find } from 'lodash'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import Actions from '../../../../../actions'
import { EMAR_FREQ_PRESETS } from '../../../../../common/constants'
import EditMedicationForm from '../../../../../components/forms/screens/EditMedicationForm'
import { HelpLink } from '../../../../../components/generic'
import Selectors from '../../../../../selectors'
import { history } from '../../../../../store'
import { withRouter } from '../../../../../withRouter'
import DiscontinueMedicationModal from '../../ResidentEmarV2Screens/componentsV2/modalsV2/DiscontinueMedicationModal'

const FORM_ID = 'editMedication'

const Form = (props) => {
  const {
    medication,
    resident,
    router: { params },
    initialiseForm,
    emarRoundTimings,
    openDiscontinueMedModal
  } = props

  const dispatch = useDispatch()
  const [isDiscontinueModalOpen, setIsDiscontinueModalOpen] = useState(false)

  const { data: flags, isLoading: isLoadingFlags } = useFeatureFlags({
    flagNames: ['emar_v2_chart']
  })
  const emarV2ChartEnabled = flags?.emar_v2_chart?.enabled ?? false

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    initialiseForm(medication, emarRoundTimings)
    return () => {
      dispatch(Actions.clearForm(FORM_ID))
    }
  }, [])

  const doneUrl = `/residents/${resident.id}/emar/${params.medication_id}/chart`
  const doneUrlV2 = `/residents/${resident.id}/emar/${params.medication_id}`

  const handleOpenDiscontinueModal = () => {
    if (emarV2ChartEnabled) {
      setIsDiscontinueModalOpen(true)
    } else {
      openDiscontinueMedModal(medication, resident)
    }
  }

  const handleConfirm = () => {
    if (isLoadingFlags) return
    const isEnabled = flags?.emar_v2_chart?.enabled === true
    const url = isEnabled ? doneUrlV2 : doneUrl
    history.push(url)
  }

  const handleCancel = () => {
    if (isLoadingFlags) return
    const isEnabled = flags?.emar_v2_chart?.enabled === true
    const url = isEnabled ? doneUrlV2 : doneUrl
    history.push(url)
  }

  if (!medication || !resident) return null
  if (isLoadingFlags) return <Spinner style={{ marginTop: '80px' }} />
  return (
    <>
      <EditMedicationForm
        medication={medication}
        resident={resident}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        onDelete={handleOpenDiscontinueModal}
      />
      {emarV2ChartEnabled && (
        <DiscontinueMedicationModal
          open={isDiscontinueModalOpen}
          medication={medication}
          resident={resident}
          residentMedicationId={medication?.id || params.medication_id}
          onCancel={() => setIsDiscontinueModalOpen(false)}
          onSuccess={() => {
            setIsDiscontinueModalOpen(false)
            history.push(doneUrlV2)
          }}
        />
      )}
    </>
  )
}

export function EditScreen(props) {
  const { medication, emarRoundTimings, testId } = props

  const isReady = emarRoundTimings && medication
  if (!isReady) return null
  return (
    <div
      data-testid={
        testId
          ? `${testId}-resident-med-edit-screen`
          : 'resident-med-edit-screen'
      }
    >
      <Form {...props} />
      <div style={styles.helpLinkContainer}>
        <HelpLink supportLink="https://support.logmycare.co.uk/en/collections/2749236-emar" />
      </div>
    </div>
  )
}

EditScreen.propTypes = {
  emarRoundTimings: PropTypes.array.isRequired,
  initialiseForm: PropTypes.func.isRequired,
  medication: PropTypes.object.isRequired,
  openDiscontinueMedModal: PropTypes.func.isRequired,
  resident: PropTypes.object.isRequired,
  router: PropTypes.object.isRequired,
  testId: PropTypes.string
}

const styles = {
  helpLinkContainer: {
    marginTop: 24
  }
}

const mapStateToProps = (state, props) => ({
  emarRoundTimings: state.data['emar-round-timings'],
  medication: find(Selectors.getResidentMarChart(state, props), {
    id: props.router.params.medication_id
  }),
  resident: Selectors.getResidentProfile(state, {
    id: props.router.params.id
  })
})

const mapDispatchToProps = (dispatch) => ({
  initialiseForm: (doc, roundTimings) => {
    const emarRoundsDetails = chain(roundTimings)
      .orderBy((timing) => timing.time)
      .map((timing) => {
        const existing = find(doc.roundTimings, { emarRoundTiming: timing.id })
        return {
          emarRoundTiming: timing.id,
          time: timing?.time || '',
          title: timing?.title || '',
          dose: existing ? existing.dose : null,
          altDose: existing ? existing.altDose : null,
          isChecked: !!existing,
          deleted: timing.deleted
        }
      })
      .filter((timing) => timing.isChecked || !timing.deleted)
      .value()

    const repeat = find(EMAR_FREQ_PRESETS, {
      freqInterval: `${doc.freqInterval}`,
      freqUnit: doc.freqUnit
    })

    const freqDaysInWeek =
      doc.freqDaysInWeek && doc.freqDaysInWeek.length
        ? doc.freqDaysInWeek
        : null

    const customFreq = !!freqDaysInWeek

    dispatch(
      Actions.addForm({
        formId: FORM_ID,
        medication: doc.medication,
        initial: {
          ...doc,
          medication: doc.medication.id,
          hasEndDate: !!doc.endDate,
          control: doc.medication.control,
          repeat: repeat && !customFreq ? repeat.key : 'custom',
          customFreq,
          freqDaysInWeek,
          emarRoundsDetails
        }
      })
    )
  },
  openDiscontinueMedModal: (medication, resident) => {
    dispatch(
      Actions.openModal({
        data: { medication, resident },
        modalId: 'discontinueMed'
      })
    )
    dispatch(Actions.addForm({ formId: 'discontinueMed' }))
  }
  /*   openMedicationQuantityModal: (medication) => {
    dispatch(
      Actions.openModal({
        data: { medication },
        modalId: 'medicationQuantity',
      })
    )
    dispatch(Actions.addForm({ formId: 'medicationQuantity' }))
  }, */
})

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(EditScreen)
)
